import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
  Alert,
  useTheme,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { FileDownload as FileDownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import PivotTableUI from 'react-pivottable';
import 'react-pivottable/pivottable.css';
import * as XLSX from 'xlsx';
import comprasInformesService, { ComprasRpt01Row } from '../../services/comprasInformesService';
import InformesBackButton from '../../components/shared/InformesBackButton';

/** Claves en camelCase tal como llegan del API; el pivot usa los mismos nombres de atributo. */
function rowToPivotRecord(row: ComprasRpt01Row): Record<string, unknown> {
  return { ...row };
}

const InformeComprasDinamico: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [fechaDesde, setFechaDesde] = useState(firstDayOfMonth.toISOString().split('T')[0]);
  const [fechaHasta, setFechaHasta] = useState(lastDayOfMonth.toISOString().split('T')[0]);
  const [nroProveedor, setNroProveedor] = useState('');
  const [codigoArticulo, setCodigoArticulo] = useState('');
  const [nroTipoFormulario, setNroTipoFormulario] = useState('');
  const [nroSuc, setNroSuc] = useState('');
  const [maxRows, setMaxRows] = useState('5000');

  const [rows, setRows] = useState<ComprasRpt01Row[]>([]);
  const [totalMatched, setTotalMatched] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pivotState, setPivotState] = useState<Record<string, unknown>>({});

  const pivotData = useMemo(() => rows.map(rowToPivotRecord), [rows]);

  const handleCargar = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Indicá fecha desde y hasta.', { variant: 'warning' });
      return;
    }
    const max = parseInt(maxRows, 10);
    if (Number.isNaN(max) || max < 1) {
      enqueueSnackbar('Máx. filas inválido.', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const res = await comprasInformesService.getRptCompras01({
        fechaDesde,
        fechaHasta,
        nroProveedor: nroProveedor.trim() ? parseInt(nroProveedor, 10) : undefined,
        codigoArticulo: codigoArticulo.trim() || undefined,
        nroTipoFormulario: nroTipoFormulario.trim() || undefined,
        nroSuc: nroSuc.trim() ? parseInt(nroSuc, 10) : undefined,
        maxRows: Math.min(max, 15000),
      });

      setRows(res.data);
      setTotalMatched(res.totalMatched);
      setTruncated(res.truncated);

      setPivotState({
        rows: ['proNombre'],
        cols: ['tipDescripcion'],
        vals: ['subtotalFinal'],
        aggregatorName: 'Sum',
        rendererName: 'Table',
      });

      if (res.truncated) {
        enqueueSnackbar(
          `Se cargaron ${res.data.length} filas de ${res.totalMatched} coincidentes. Ajustá filtros o el máximo de filas.`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar(`${res.data.length} filas cargadas.`, { variant: 'success' });
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudieron cargar los datos del informe.', { variant: 'error' });
      setRows([]);
      setTotalMatched(0);
      setTruncated(false);
      setPivotState({});
    } finally {
      setLoading(false);
    }
  }, [
    fechaDesde,
    fechaHasta,
    nroProveedor,
    codigoArticulo,
    nroTipoFormulario,
    nroSuc,
    maxRows,
    enqueueSnackbar,
  ]);

  const handleExportXlsx = () => {
    if (rows.length === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows as unknown as Record<string, unknown>[]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Compras');
    const name = `informe-compras-dinamico-${fechaDesde}_${fechaHasta}.xlsx`;
    XLSX.writeFile(wb, name);
  };

  const [deferUpdates, setDeferUpdates] = useState(true);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <InformesBackButton to="/compras/informes" useBrowserBack />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                Informe dinámico de compras
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                Arrastrá campos a Filas, Columnas y Filtros, y elegí valores a agregar.
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Fecha desde"
                name="fechaDesde"
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Fecha hasta"
                name="fechaHasta"
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Nº proveedor"
                value={nroProveedor}
                onChange={(e) => setNroProveedor(e.target.value)}
                fullWidth
                size="small"
                placeholder="Opcional"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Código artículo"
                value={codigoArticulo}
                onChange={(e) => setCodigoArticulo(e.target.value)}
                fullWidth
                size="small"
                placeholder="Contiene"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Tipo formulario"
                value={nroTipoFormulario}
                onChange={(e) => setNroTipoFormulario(e.target.value)}
                fullWidth
                size="small"
                placeholder="Opcional"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Nº sucursal"
                value={nroSuc}
                onChange={(e) => setNroSuc(e.target.value)}
                fullWidth
                size="small"
                placeholder="Opcional"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Máx. filas"
                value={maxRows}
                onChange={(e) => setMaxRows(e.target.value)}
                fullWidth
                size="small"
                helperText="Máx. 15000"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deferUpdates}
                    onChange={(e) => setDeferUpdates(e.target.checked)}
                    size="small"
                  />
                }
                label="Diferir actualización del pivot (mejor con muchas filas)"
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                  onClick={() => void handleCargar()}
                  disabled={loading}
                >
                  Cargar datos
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportXlsx}
                  disabled={rows.length === 0}
                >
                  Exportar Excel (dataset)
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {truncated && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Resultado truncado: hay {totalMatched} filas que coinciden; se muestran {rows.length}. Reducí el rango o
              aumentá el máximo (hasta 15000).
            </Alert>
          )}

          {!truncated && rows.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {rows.length} fila(s) cargada(s)
              {totalMatched !== rows.length ? ` · ${totalMatched} coincidencias en servidor` : ''}
            </Typography>
          )}

          {loading && rows.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4 }}>
              Configurá filtros y pulsá «Cargar datos».
            </Typography>
          ) : (
            <Box
              sx={{
                '& .pvtUi': {
                  color: 'text.primary',
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '0.875rem',
                },
                '& .pvtAxisContainer, & .pvtVals': {
                  backgroundColor: '#fafafa',
                },
                '& table.pvtTable': {
                  fontSize: '0.8125rem',
                },
                overflow: 'auto',
                maxHeight: { xs: '60vh', md: '70vh' },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <PivotTableUI
                data={pivotData}
                onChange={(s) => {
                  const next = { ...(s as Record<string, unknown>) };
                  delete next.data;
                  setPivotState(next);
                }}
                deferUpdates={deferUpdates}
                {...pivotState}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default InformeComprasDinamico;
